import { useState, useEffect } from 'react';

interface UserLevelProps {
  level: number;
  experience: number;
  nextLevelExperience?: number;
}

const UserLevel = ({ level, experience, nextLevelExperience }: UserLevelProps) => {
  const [width, setWidth] = useState(0);
  const [lastExperience, setLastExperience] = useState(experience);
  const [lastLevel, setLastLevel] = useState(level);
  
  // Calculate the XP needed for the next level based on the formula: 1000 * level
  const calculatedNextLevelXP = level * 1000;
  // Use the provided nextLevelExperience or calculate it if not provided
  const targetXP = nextLevelExperience || calculatedNextLevelXP;
  
  // Track when the experience or level changes
  useEffect(() => {
    // Check if experience or level has changed
    if (experience !== lastExperience || level !== lastLevel) {
      console.log(`UserLevel component: Experience/level changed from ${lastExperience}/${lastLevel} to ${experience}/${level}`);
      
      // Update last values
      setLastExperience(experience);
      setLastLevel(level);
      
      // Reset width to 0 to trigger animation
      setWidth(0);
    }
  }, [experience, level, lastExperience, lastLevel]);
  
  // Animate progress bar filling
  useEffect(() => {
    const progress = Math.min(100, (experience / targetXP) * 100);
    
    // First reset to 0 if we just received new data
    if (width === 0) {
      // Wait a short moment then set the width to trigger animation
      const timer = setTimeout(() => setWidth(progress), 100);
      return () => clearTimeout(timer);
    }
    // If width is already set, update it directly
    else if (width !== progress) {
      setWidth(progress);
    }
  }, [experience, targetXP, width]);
  
  return (
    <div className="w-full">
      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
        <div className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-base sm:text-lg md:text-xl border-2 sm:border-4 border-vibebops-card flex-shrink-0">
          {level}
        </div>
        <div>
          <h3 className="font-semibold text-sm sm:text-base md:text-lg">Level {level}</h3>
          <p className="text-vibebops-textMuted text-xs sm:text-sm">
            {experience} / {targetXP} XP to level {level + 1}
          </p>
        </div>
      </div>
      
      <div className="h-1.5 sm:h-2 bg-vibebops-highlight rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-violet-500 to-pink-500 transition-all duration-1000 ease-out"
          style={{ width: `${width}%` }}
        ></div>
      </div>
      
      <div className="mt-2 sm:mt-3 md:mt-4">
        <h4 className="font-medium mb-1 sm:mb-2 text-xs sm:text-sm md:text-base">Earn XP by:</h4>
        <ul className="text-xs sm:text-sm text-vibebops-textMuted space-y-0.5 sm:space-y-1">
          <li className="flex items-center gap-1 sm:gap-1.5 md:gap-2">
            <span className="h-1 w-1 sm:h-1.5 sm:w-1.5 md:h-2 md:w-2 rounded-full bg-vibebops-accent flex-shrink-0"></span>
            <span>Creating posts (+10 XP)</span>
          </li>
          <li className="flex items-center gap-1 sm:gap-1.5 md:gap-2">
            <span className="h-1 w-1 sm:h-1.5 sm:w-1.5 md:h-2 md:w-2 rounded-full bg-vibebops-accent flex-shrink-0"></span>
            <span>Receiving likes (+2 XP each)</span>
          </li>
          <li className="flex items-center gap-1 sm:gap-1.5 md:gap-2">
            <span className="h-1 w-1 sm:h-1.5 sm:w-1.5 md:h-2 md:w-2 rounded-full bg-vibebops-accent flex-shrink-0"></span>
            <span>Getting upvotes (+3 XP each)</span>
          </li>
          <li className="flex items-center gap-1 sm:gap-1.5 md:gap-2">
            <span className="h-1 w-1 sm:h-1.5 sm:w-1.5 md:h-2 md:w-2 rounded-full bg-vibebops-accent flex-shrink-0"></span>
            <span>Getting comments (+5 XP each)</span>
          </li>
          <li className="flex items-center gap-1 sm:gap-1.5 md:gap-2">
            <span className="h-1 w-1 sm:h-1.5 sm:w-1.5 md:h-2 md:w-2 rounded-full bg-vibebops-accent flex-shrink-0"></span>
            <span>Daily login (+5 XP)</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default UserLevel;
